import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import '../../component-styles/ReportsShared.css';
import '../../component-styles/TimeBillingReport.css';
import '../../component-styles/TimeBilling.css';
import API_BASE from '../../utils/api.js';
import { exportRowsToExcel } from '../../utils/excelExport.js';
import usePermissions from '../../auth/usePermissions';
import GlobalTimeEntryModal from './GlobalTimeEntryModal';

/* --------------------------------------------------------
   Helpers
-------------------------------------------------------- */
function fmtUSD(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  });
}

function getDefaultDateRange() {
  const year = new Date().getFullYear();
  return { start: `${year}-01-01`, end: `${year}-12-31` };
}

/* ========================================================
   TIME BILLING REPORT PAGE
======================================================== */
export default function TimeBillingReportPage() {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const canEditEntries = permissions.editTeamTimeEntries !== false;

  const userId = localStorage.getItem('userId');
  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  /* ---------------- Filters ---------------- */
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [projectFilter, setProjectFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [billableFilter, setBillableFilter] = useState('all');

  /* ---------------- Modal ---------------- */
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [modalEntry, setModalEntry] = useState(null);

  /* --------------------------------------------------------
     Fetch report
  -------------------------------------------------------- */
  useEffect(() => {
    if (!userId) return;

    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE}/api/reports/time-billing?startDate=${startDate}&endDate=${endDate}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('idToken')}`,
            },
          }
        );

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        setRows(Array.isArray(data.rows) ? data.rows : []);
      } catch (err) {
        console.error('Failed to load time billing report', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, API_BASE, startDate, endDate, refreshKey]);

  function refresh() {
    setRefreshKey((k) => k + 1);
  }

  function openCreateModal() {
    setModalMode('create');
    setModalEntry(null);
    setModalOpen(true);
  }

  function openEditModal(row) {
    setModalMode('edit');
    setModalEntry(row);
    setModalOpen(true);
  }

  async function deleteRow(row) {
    if (!row.sortKey) return;
    if (!window.confirm('Delete this time entry? This cannot be undone.')) return;

    try {
      const res = await fetch(
        `${API_BASE}/api/timebilling/${encodeURIComponent(userId)}/${encodeURIComponent(row.sortKey)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      refresh();
    } catch (err) {
      console.error('Failed to delete time entry', err);
    }
  }

  /* --------------------------------------------------------
     Derived Data
  -------------------------------------------------------- */
  const projects = useMemo(() => {
    const unique = new Map();
    rows.forEach((r) => {
      const id = String(r.projectId || '');
      if (!id || unique.has(id)) return;
      unique.set(id, r.projectName || 'Untitled Project');
    });
    return Array.from(unique.entries()).map(([id, name]) => ({ id, name }));
  }, [rows]);

  const employees = useMemo(() => {
    const unique = new Set();
    rows.forEach((r) => {
      if (r.teamMember) unique.add(r.teamMember);
    });
    return Array.from(unique).sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (projectFilter && String(r.projectId || '') !== projectFilter) return false;
      if (employeeFilter && r.teamMember !== employeeFilter) return false;
      if (billableFilter === 'billable' && r.billable === false) return false;
      if (billableFilter === 'nonbillable' && r.billable !== false) return false;
      return true;
    });
  }, [rows, projectFilter, employeeFilter, billableFilter]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => {
        const hrs = Number(r.time || 0);
        acc.hours += hrs;
        if (r.billable !== false) acc.billableHours += hrs;
        else acc.nonBillableHours += hrs;
        acc.employee += Number(r.employeeAmount || 0);
        acc.billable += Number(r.billableAmount || 0);
        return acc;
      },
      { hours: 0, billableHours: 0, nonBillableHours: 0, employee: 0, billable: 0 }
    );
  }, [filteredRows]);

  /* --------------------------------------------------------
     Actions
  -------------------------------------------------------- */
  function exportExcel() {
    const headers = [
      'Project ID',
      'Project',
      'Team Member',
      'Date',
      'Invoice',
      'Description',
      'Billable',
      'Hours',
      'Employee Rate',
      'Project Rate',
      'Employee Amount',
      'Billable Amount',
    ];

    const rowsForExport = filteredRows.map((r) => [
      r.projectId || '',
      r.projectName || '',
      r.teamMember || '',
      r.date || '',
      r.invoiceNumber || '',
      r.description || '',
      r.billable !== false ? 'Yes' : 'No',
      Number(r.time || 0),
      Number(r.employeeRate || 0),
      Number(r.projectRate || 0),
      Number(r.employeeAmount || 0),
      Number(r.billableAmount || 0),
    ]);

    exportRowsToExcel({
      filename: 'time-billing-report.xlsx',
      sheetName: 'Time Billing',
      headers,
      rows: rowsForExport,
    });
  }

  function exportPDF() {
    const doc = new jsPDF('l', 'pt'); // landscape for wide table

    doc.setFontSize(16);
    doc.text('Time Billing Report', 40, 40);

    doc.setFontSize(10);
    doc.text(`${startDate} - ${endDate}`, 40, 58);

    autoTable(doc, {
      startY: 80,
      head: [
        [
          'Project',
          'Team Member',
          'Date',
          'Invoice',
          'Description',
          'Billable',
          'Time',
          'Employee Rate',
          'Project Rate',
          'Employee Amount',
          'Billable Amount',
        ],
      ],
      body: filteredRows.map((r) => [
        r.projectName,
        r.teamMember,
        r.date,
        r.invoiceNumber || '',
        r.description || '',
        r.billable !== false ? 'Yes' : 'No',
        Number(r.time).toFixed(2),
        fmtUSD(r.employeeRate),
        fmtUSD(r.projectRate),
        fmtUSD(r.employeeAmount),
        fmtUSD(r.billableAmount),
      ]),
      styles: {
        fontSize: 9,
        cellPadding: 6,
      },
      headStyles: {
        fillColor: [17, 24, 39],
        textColor: 255,
      },
      columnStyles: {
        6: { halign: 'right' },
        7: { halign: 'right' },
        8: { halign: 'right' },
        9: { halign: 'right' },
        10: { halign: 'right' },
      },
    });

    const finalY = doc.lastAutoTable.finalY + 20;
    doc.setFontSize(11);
    doc.text(
      `Totals - Hours: ${totals.hours.toFixed(2)} | Employee: ${fmtUSD(
        totals.employee
      )} | Billable: ${fmtUSD(totals.billable)}`,
      40,
      finalY
    );

    doc.save('time-billing-report.pdf');
  }

  function clearFilters() {
    setProjectFilter('');
    setEmployeeFilter('');
    setBillableFilter('all');
  }

  if (loading) {
    return (
      <div className="report-shell">
        <div className="report-shell-inner">
          <div className="report-empty">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="report-shell">
      <div className="report-shell-inner">
        <div className="time-report-page fade-in">
          {/* =====================================================
             HEADER
          ===================================================== */}
          <div className="time-report-header report-header slide-down">
            <div className="time-report-title report-title">
              <h1>Time Billing Hub</h1>
              <div className="muted">
                {startDate} - {endDate}
              </div>
            </div>

            <div className="time-report-controls report-filters">
              <select
                className="report-select"
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
              >
                <option value="">All Projects</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>

              <select
                className="report-select"
                value={employeeFilter}
                onChange={(e) => setEmployeeFilter(e.target.value)}
              >
                <option value="">All Team Members</option>
                {employees.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>

              <select
                className="report-select"
                value={billableFilter}
                onChange={(e) => setBillableFilter(e.target.value)}
              >
                <option value="all">Billable + Non-Billable</option>
                <option value="billable">Billable Only</option>
                <option value="nonbillable">Non-Billable Only</option>
              </select>

              <input
                className="report-input"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <input
                className="report-input"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />

              {canEditEntries && (
                <button className="timebillingreport-primary-btn" onClick={openCreateModal}>
                  + Log Time
                </button>
              )}
            </div>
          </div>

          {/* =====================================================
             TABLE
          ===================================================== */}
          <div className="time-report-table-wrap report-table-wrap">
            <table className="time-report-table report-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Team Member</th>
                  <th>Date</th>
                  <th>Invoice</th>
                  <th>Description</th>
                  <th>Billable</th>
                  <th className="right">Time</th>
                  <th className="right">Employee Rate</th>
                  <th className="right">Project Rate</th>
                  <th className="right">Employee Amount</th>
                  <th className="right">Billable Amount</th>
                  {canEditEntries && <th />}
                </tr>
              </thead>

              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={canEditEntries ? 12 : 11} className="empty">
                      No time entries found
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((r, i) => (
                    <tr key={i} style={{ animationDelay: `${i * 20}ms`, opacity: r.billable === false ? 0.7 : 1 }}>
                      <td>{r.projectName}</td>
                      <td>{r.teamMember}</td>
                      <td>{r.date}</td>
                      <td>
                        {r.projectId && r.tbInvoiceId && r.invoiceNumber ? (
                          <a href={`/projects/${r.projectId}/timebilling-invoices/${r.tbInvoiceId}`}>
                            {r.invoiceNumber}
                          </a>
                        ) : (
                          r.invoiceNumber || '-'
                        )}
                      </td>
                      <td>{r.description}</td>
                      <td>
                        {r.billable !== false ? (
                          <span className="tb-badge tb-badge-billable">Yes</span>
                        ) : (
                          <span className="tb-badge tb-badge-nonbillable">No</span>
                        )}
                      </td>
                      <td className="right">{Number(r.time).toFixed(2)}</td>
                      <td className="right">{fmtUSD(r.employeeRate)}</td>
                      <td className="right">{fmtUSD(r.projectRate)}</td>
                      <td className="right">{fmtUSD(r.employeeAmount)}</td>
                      <td className="right">{fmtUSD(r.billableAmount)}</td>
                      {canEditEntries && (
                        <td className="right">
                          {r.invoiceNumber ? (
                            <span className="muted" title="Billed entries are locked">
                              Billed
                            </span>
                          ) : (
                            <>
                              <button className="link-btn" onClick={() => openEditModal(r)}>
                                Edit
                              </button>{' '}
                              <button className="link-btn danger" onClick={() => deleteRow(r)}>
                                Delete
                              </button>
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>

              <tfoot>
                <tr>
                  <td colSpan={4} />
                  <td style={{ fontSize: '0.8rem', color: '#666' }}>
                    Billable: {totals.billableHours.toFixed(2)}h &nbsp;|&nbsp; Non-Bill.: {totals.nonBillableHours.toFixed(2)}h
                  </td>
                  <td />
                  <td className="right">
                    <strong>{totals.hours.toFixed(2)}</strong>
                  </td>
                  <td />
                  <td />
                  <td className="right">
                    <strong>{fmtUSD(totals.employee)}</strong>
                  </td>
                  <td className="right">
                    <strong>{fmtUSD(totals.billable)}</strong>
                  </td>
                  {canEditEntries && <td />}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* =====================================================
             FOOTER ACTIONS
          ===================================================== */}
          <div className="time-report-footer slide-up">
            <button className="timebillingreport-secondary-btn" onClick={() => navigate('/reports')}>
              Cancel
            </button>

            <button className="timebillingreport-secondary-btn" onClick={clearFilters}>
              Clear Filters
            </button>

            <button className="timebillingreport-secondary-btn" onClick={exportPDF}>
              Share / PDF
            </button>

            <button className="timebillingreport-primary-btn" onClick={exportExcel}>
              Export Excel
            </button>
          </div>
        </div>
      </div>

      <GlobalTimeEntryModal
        open={modalOpen}
        mode={modalMode}
        entry={modalEntry}
        onClose={() => setModalOpen(false)}
        onSaved={refresh}
      />
    </div>
  );
}
